/**
 * NotificationBell Component
 * Bell icon with badge showing unread count and dropdown panel
 */
import React, { useState, useRef } from 'react';
import {
  IconButton,
  Badge,
  Popper,
  ClickAwayListener,
  Grow,
  Tooltip
} from '@mui/material';
import { Notifications as NotificationsIcon } from '@mui/icons-material';
import { useNotifications } from '../../contexts/NotificationContext';
import NotificationPanel from './NotificationPanel';

const NotificationBell = ({ sx = {} }) => {
  const { unreadCount, fetchNotifications } = useNotifications();
  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);

  const handleToggle = async () => {
    // Fetch latest notifications when opening the panel
    if (!open) {
      await fetchNotifications();
    }
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  return (
    <>
      <Tooltip title="Notifications" arrow>
        <IconButton
          ref={anchorRef}
          onClick={handleToggle}
          size="medium"
          sx={{
            px: 1.5,
            py: 1,
            color: 'inherit',
            ...sx
          }}
        >
          <Badge
            badgeContent={unreadCount}
            color="error"
            max={99}
            sx={{
              '& .MuiBadge-badge': {
                fontSize: '0.75rem',
                height: 20,
                minWidth: 20,
                padding: '0 5px',
                fontWeight: 700,
                right: 3,
                top: 3,
                animation: unreadCount > 0 ? 'pulse 2s infinite' : 'none',
                '@keyframes pulse': {
                  '0%': {
                    transform: 'scale(1)',
                    opacity: 1
                  },
                  '50%': {
                    transform: 'scale(1.1)',
                    opacity: 0.9
                  },
                  '100%': {
                    transform: 'scale(1)',
                    opacity: 1
                  }
                }
              }
            }}
          >
            <NotificationsIcon
              sx={{
                fontSize: 28,
                transition: 'transform 0.2s ease',
                transform: open ? 'rotate(20deg)' : 'rotate(0deg)'
              }}
            />
          </Badge>
        </IconButton>
      </Tooltip>

      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement="bottom-end"
        transition
        disablePortal={false}
        sx={{
          zIndex: 1300,
          mt: 1
        }}
        modifiers={[
          {
            name: 'offset',
            options: {
              offset: [0, 8]
            }
          }
        ]}
      >
        {({ TransitionProps }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin: 'right top'
            }}
          >
            <div>
              <ClickAwayListener onClickAway={handleClose}>
                <div>
                  <NotificationPanel onClose={() => setOpen(false)} />
                </div>
              </ClickAwayListener>
            </div>
          </Grow>
        )}
      </Popper>
    </>
  );
};

export default NotificationBell;
